/**
 * Modern-theme panels for the customer Profile page (Appearance, Account,
 * Security). Logic comes from the shared hooks in ProfilePage.js.
 */
import { CheckCircle, Lock, Mail, Palette, ShieldCheck } from 'lucide-react';
import { useSecurityForm, useThemeChoice } from './ProfilePage';

export function ModernCard({ title, icon: Icon, aside, children, testid }) {
  return (
    <section className="am-card p-5 sm:p-6" data-testid={testid}>
      <div className="flex items-center justify-between gap-3 mb-4">
        <h2 className="am-section-title flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4" style={{ color: 'var(--am-emerald-600)' }} />}{title}
        </h2>
        {aside}
      </div>
      {children}
    </section>
  );
}

export function Field({ label, hint, htmlFor, children }) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={htmlFor} className="am-label block">{label}</label>
      {children}
      {hint && <p className="text-xs" style={{ color: 'var(--am-ink-3)' }}>{hint}</p>}
    </div>
  );
}

function Swatch({ id, label, sub, active, disabled, onClick, preview }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} data-testid={`theme-swatch-${id}`}
      className="text-left rounded-[14px] overflow-hidden border-2 transition-colors"
      style={{ borderColor: active ? 'var(--am-emerald-500)' : 'var(--am-hair)' }}>
      {preview}
      <div className="px-3 py-2 flex items-center justify-between bg-white">
        <div>
          <div className="text-[13px] font-semibold" style={{ color: 'var(--am-ink-1)' }}>{label}</div>
          <div className="text-[11px]" style={{ color: 'var(--am-ink-3)' }}>{sub}</div>
        </div>
        {active && <CheckCircle className="w-4 h-4" style={{ color: 'var(--am-emerald-600)' }} data-testid={`theme-active-${id}`} />}
      </div>
    </button>
  );
}

export function ModernAppearancePanel() {
  const { theme, saving, choose, toggleVisible } = useThemeChoice();
  if (!toggleVisible) return null;
  return (
    <ModernCard title="Appearance" icon={Palette} testid="theme-toggle-panel">
      <div className="grid grid-cols-2 gap-3">
        <Swatch id="modern" label="Modern" sub="Light, card layout" active={theme === 'modern'} disabled={saving}
          onClick={() => choose('modern')}
          preview={<div className="h-14 relative" style={{ background: 'linear-gradient(135deg,#F7F5F0,#FFFFFF)' }}>
            <span className="absolute left-3 top-3 text-xs font-semibold" style={{ color: '#047857' }}>Orders</span>
            <span className="absolute right-3 top-3 h-2.5 w-8 rounded" style={{ background: '#B08D57' }} />
          </div>} />
        <Swatch id="code" label="Code" sub="Classic terminal" active={theme === 'code'} disabled={saving}
          onClick={() => choose('code')}
          preview={<div className="h-14 relative" style={{ background: 'linear-gradient(135deg,#050003,#1A0A2A)' }}>
            <span className="absolute left-3 top-1/2 -translate-y-1/2 font-mono text-[10px]" style={{ color: '#39FF14' }}>&gt; ORDERS_</span>
          </div>} />
      </div>
      {saving && <p className="text-xs mt-3" style={{ color: 'var(--am-ink-3)' }}>Saving…</p>}
    </ModernCard>
  );
}

export function ModernAccountInfo({ user }) {
  const rows = [
    ['Email', user?.email],
    ['Account type', <span className="am-chip am-chip-emerald capitalize">{user?.role}</span>],
    ['Member since', user?.created_at ? new Date(user.created_at).toLocaleDateString(undefined, { month: 'long', year: 'numeric' }) : 'N/A'],
  ];
  return (
    <ModernCard title="Account" testid="account-info">
      <dl className="divide-y" style={{ borderColor: 'var(--am-hair)' }}>
        {rows.map(([k, v]) => (
          <div key={k} className="flex items-center justify-between gap-3 py-3 text-sm" style={{ borderColor: 'var(--am-hair)' }}>
            <dt style={{ color: 'var(--am-ink-3)' }}>{k}</dt>
            <dd className="truncate text-right" style={{ color: 'var(--am-ink-1)' }}>{v}</dd>
          </div>
        ))}
      </dl>
    </ModernCard>
  );
}

function PwInput({ value, onChange, placeholder, testid, autoComplete, type = 'password' }) {
  return <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
    className="am-input" data-testid={testid} autoComplete={autoComplete} />;
}

export function ModernSecurityPanel() {
  const f = useSecurityForm();
  const close = () => { f.reset(); f.setMode(null); };
  return (
    <ModernCard title="Security" icon={ShieldCheck} testid="security-panel">
      {f.mode === null && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <button type="button" className="am-btn-ghost justify-start" onClick={() => { f.reset(); f.setMode('password'); }} data-testid="security-open-change-password">
            <Lock className="w-4 h-4" />Change password
          </button>
          <button type="button" className="am-btn-ghost justify-start" onClick={() => { f.reset(); f.setMode('email'); }} data-testid="security-open-change-email">
            <Mail className="w-4 h-4" />Change email
          </button>
        </div>
      )}
      {f.mode === 'password' && (
        <div className="space-y-3">
          <p className="text-sm" style={{ color: 'var(--am-ink-3)' }}>Confirm it's you, then choose a new password (8+ characters).</p>
          <PwInput value={f.currentPassword} onChange={f.setCurrentPassword} placeholder="Current password" testid="security-current-password" autoComplete="current-password" />
          <PwInput value={f.newPassword} onChange={f.setNewPassword} placeholder="New password" testid="security-new-password" autoComplete="new-password" />
          <PwInput value={f.confirmPassword} onChange={f.setConfirmPassword} placeholder="Confirm new password" testid="security-confirm-password" autoComplete="new-password" />
          <div className="flex gap-2">
            <button type="button" onClick={f.handleChangePassword} className="am-cta flex-1 h-11 text-sm"
              disabled={f.busy || !f.currentPassword || !f.newPassword || !f.confirmPassword} data-testid="security-submit-password">
              {f.busy ? 'Updating…' : 'Update password'}
            </button>
            <button type="button" onClick={close} className="am-btn-ghost" data-testid="security-cancel-password">Cancel</button>
          </div>
        </div>
      )}
      {f.mode === 'email' && (
        <div className="space-y-3">
          <p className="text-sm" style={{ color: 'var(--am-ink-3)' }}>Re-enter your password, then set your new email.</p>
          <PwInput value={f.currentPassword} onChange={f.setCurrentPassword} placeholder="Current password" testid="security-email-current-password" autoComplete="current-password" />
          <PwInput type="email" value={f.newEmail} onChange={f.setNewEmail} placeholder="New email" testid="security-new-email" autoComplete="email" />
          <div className="flex gap-2">
            <button type="button" onClick={f.handleChangeEmail} className="am-cta flex-1 h-11 text-sm"
              disabled={f.busy || !f.currentPassword || !f.newEmail} data-testid="security-submit-email">
              {f.busy ? 'Updating…' : 'Update email'}
            </button>
            <button type="button" onClick={close} className="am-btn-ghost" data-testid="security-cancel-email">Cancel</button>
          </div>
        </div>
      )}
    </ModernCard>
  );
}
